
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import * as Sentry from "@sentry/react";
import { useAuth, type SignupSource } from "@/contexts/AuthContext";
import SignInForm from "@/components/auth/SignInForm";
import SignUpForm from "@/components/auth/SignUpForm";
import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";
import MemorialCreationFlow from "@/components/MemorialCreationFlow";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuthTokens } from "@/hooks/useAuthTokens";
import { getPublicUrl } from "@/utils/domain";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const memorialId = searchParams.get('memorialId');
  const isNFCActivation = searchParams.get('action') === 'activate' && memorialId;
  const isB2BFlow = searchParams.get('b2b') === 'true';
  const b2bClaimMemorialId = searchParams.get('b2b_claim'); // New B2B claim flow
  const invitationId = searchParams.get('invitation_id');
  const invitationAction = searchParams.get('action');
  const prefillEmail = searchParams.get('email');
  const prefillPhone = searchParams.get('phone');
  const forceLogin = searchParams.get('login') === 'true';
  const forceSignup = searchParams.get('signup') === 'true';

  const [isLogin, setIsLogin] = useState(
    forceLogin ? true : forceSignup ? false : true
  );
  const [showMemorialCreation, setShowMemorialCreation] = useState(false);
  const [showTokenRedemption, setShowTokenRedemption] = useState(false);
  const [redemptionToken, setRedemptionToken] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [signupToken, setSignupToken] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading,
    signIn,
    signInWithGoogle,
    signUp,
    ensureAdminRecord,
    user,
    redeemToken,
    inPasswordRecovery
  } = useAuth();
  const { claimPendingTokensByEmail } = useAuthTokens();
  const [isSigningUp, setIsSigningUp] = useState(false);

  const authBgUrl = "/lovable-uploads/auth-background-polaroids.png";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl && !isNFCActivation) {
      setRedemptionToken(tokenFromUrl);
      if (!isAuthenticated && !isLoading) {
        setSignupToken(tokenFromUrl);
      } else if (isAuthenticated) {
        setShowTokenRedemption(true);
      }
    }
  }, [isNFCActivation, isAuthenticated, isLoading]);

  useEffect(() => {
    const handleAuthenticatedUser = async () => {
      // Don't redirect during password recovery flow
      if (inPasswordRecovery) return;
      if (!isAuthenticated || isLoading || !user) return;

      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');

      // If this is an invitation, redirect back to memorials with invitation params
      if (invitationId && invitationAction) {
        navigate(`/memorials?action=${invitationAction}&invitation_id=${invitationId}`, { replace: true });
        return;
      }

      // NEW: Handle B2B claim flow from /activate page
      if (b2bClaimMemorialId) {
        // Try to claim any B2B tokens for this user's email
        if (user.email) {
          await claimPendingTokensByEmail(user.email, user.id);
        }

        // Navigate directly to memorials - NO activation popup
        navigate('/memorials', { replace: true });
        return;
      }

      // If this is NFC activation, redirect to memorials with activate parameter
      if (isNFCActivation) {
        // For B2B flow (legacy URL), claim the memorial and redirect
        if (isB2BFlow && memorialId) {
          // Try to claim any B2B tokens for this user's email
          if (user.email) {
            await claimPendingTokensByEmail(user.email, user.id);
          }

          navigate(`/memorials`, { replace: true });
          return;
        }

        // For DTC/NFC flow, use activation popup
        const rawPurpose = searchParams.get('memorialPurpose');
        const normalizedPurpose = rawPurpose?.replace('-', '_');
        const purposeParam = normalizedPurpose ? `&memorialPurpose=${normalizedPurpose}` : '';
        navigate(`/memorials?activate=${memorialId}${purposeParam}`, { replace: true });
        return;
      }

      if (tokenFromUrl && !isNFCActivation) {
        setRedemptionToken(tokenFromUrl);
        setShowTokenRedemption(true);
      } else if (!showMemorialCreation && !showTokenRedemption) {
        navigate("/memorials", {
          replace: true
        });
      }
    };

    handleAuthenticatedUser();
  }, [isAuthenticated, isLoading, navigate, showMemorialCreation, showTokenRedemption, isNFCActivation, memorialId, invitationId, invitationAction, isB2BFlow, user, claimPendingTokensByEmail, b2bClaimMemorialId, inPasswordRecovery, searchParams]);

  const handleSignIn = async (identifier: string, password: string) => {
    try {
      await signIn(identifier, password);
    } catch (error) {
      console.error("Login error in handleSignIn:", error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error("Google sign in error:", error);
    }
  };

  const handleSignUp = async (phone: string, email: string, password: string, firstName: string, lastName: string) => {
    setIsSigningUp(true);
    try {
      const source: SignupSource = (b2bClaimMemorialId || isB2BFlow) ? 'b2b' : isNFCActivation ? 'nfc' : 'd2c';
      const {
        user,
        error
      } = await signUp(phone, email, password, firstName, lastName, source);

      if (error) {
        throw error;
      }

      if (user) {
        const adminOk = await ensureAdminRecord(user.id, email, firstName, lastName, phone);
        if (!adminOk) {
          Sentry.captureMessage('ensureAdminRecord returned false', {
            level: 'warning',
            extra: { userId: user.id, email, surface: 'Auth' },
          });
        }

        // Link portal/partner invitations by both email and phone
        try {
          await supabase.rpc('link_pending_portal_invitations', {
            _user_id: user.id,
            _email: email,
            _phone: phone,
          });
        } catch (linkError) {
          console.error('Error linking portal invitations:', linkError);
        }

        // Handle token redemption if signup came with a token
        if (signupToken) {
          const rawPurpose = searchParams.get('memorialPurpose');
          const normalizedPurpose = (rawPurpose?.replace('-', '_') || null) as 'self' | 'loved_one' | null;
          if (normalizedPurpose && (normalizedPurpose === 'self' || normalizedPurpose === 'loved_one')) {
            const result = await redeemToken(signupToken, normalizedPurpose);
            if (result.success) {
              toast({ title: "Token Redeemed", description: result.message });
            }
          }
        }

        setIsSigningUp(false);
        navigate("/memorials", { replace: true });
      }
    } catch (error: unknown) {
      console.error('Sign up error in handleSignUp:', error);
      setIsSigningUp(false);
    }
  };



  const toggleTokenRedemption = () => {
    setShowTokenRedemption(!showTokenRedemption);
  };

  const handleTokenRedemption = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!redemptionToken.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid token",
        variant: "destructive"
      });
      return;
    }
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to redeem a token",
        variant: "destructive"
      });
      return;
    }
    setIsRedeeming(true);
    try {
      // Get memorial purpose from URL and normalize it
      const rawPurpose = searchParams.get('memorialPurpose');
      const normalizedPurpose = (rawPurpose?.replace('-', '_') || null) as 'self' | 'loved_one' | null;
      
      if (!normalizedPurpose || (normalizedPurpose !== 'self' && normalizedPurpose !== 'loved_one')) {
        toast({
          title: "Error",
          description: "Memorial purpose is required. Please use the proper activation link.",
          variant: "destructive"
        });
        setIsRedeeming(false);
        return;
      }
      const result = await redeemToken(redemptionToken, normalizedPurpose);
      if (result.success) {
        toast({
          title: "Token Redeemed",
          description: result.message
        });
        navigate('/memorials', { replace: true });
      } else {
        toast({
          title: "Redemption Failed",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error: unknown) {
      console.error('Token redemption error:', error);
      toast({
        title: "Redemption Failed",
        description: error instanceof Error ? error.message : "Failed to redeem token. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  if (isLoading) {
    return <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>;
  }

  if (showMemorialCreation) {
    return <MemorialCreationFlow />;
  }
  // Only show token redemption for non-NFC activations when authenticated
  if (showTokenRedemption && isAuthenticated && !isNFCActivation) {
    return <div className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="absolute inset-0 z-0" style={{
        backgroundImage: `url('${authBgUrl}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }} />
        <div className="absolute inset-0 bg-black bg-opacity-60 z-0"></div>

        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6 pt-24 space-y-6 relative z-10">
          <div className="flex justify-center absolute left-0 right-0 top-6">
            <img src="/lovable-uploads/d0936c82-5b71-4ccc-a9d9-85afdf15b0a2.png" alt="Pontis Logo" className="h-20 w-auto object-contain max-w-full" />
          </div>
          
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-serif font-bold text-brand-primary">
              Redeem Your Token
            </h1>
            <p className="text-brand-secondary">
              Enter your memorial token to claim your medallions
            </p>
          </div>

          <form onSubmit={handleTokenRedemption} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="token" className="block text-sm font-medium text-gray-700">
                Memorial Token
              </label>
              <Input id="token" value={redemptionToken} onChange={e => setRedemptionToken(e.target.value)} placeholder="Enter your token" className="w-full" required />
            </div>
            
            <Button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary/90" disabled={isRedeeming}>
              {isRedeeming ? "Redeeming..." : "Redeem Token"}
            </Button>
            
            <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/memorials')}>
              Back
            </Button>
          </form>
        </div>
      </div>;
  }
  return <div className="min-h-screen flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 z-0" style={{
      backgroundImage: `url('${authBgUrl}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }} />
      
      <div className="absolute inset-0 bg-black bg-opacity-60 z-0"></div>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-4 sm:p-6 pt-16 sm:pt-24 space-y-4 sm:space-y-6 relative z-10">
        
        <div className="absolute top-4 left-4 z-20">
          <a 
            href={getPublicUrl('/')} 
            className="flex items-center text-gray-400 hover:text-brand-primary transition-colors p-2 -ml-2 rounded-full hover:bg-gray-50"
            aria-label="Back to Home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline ml-1 text-sm font-medium">Back</span>
          </a>
        </div>

        <div className="flex justify-center absolute left-0 right-0 top-3 sm:top-6 pointer-events-none">
          <img src="/lovable-uploads/d0936c82-5b71-4ccc-a9d9-85afdf15b0a2.png" alt="Pontis Logo" className="h-12 sm:h-16 md:h-20 w-auto object-contain max-w-full" />
        </div>
        
        <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-brand-primary">
              {invitationId ? "Accept Your Invitation" : isNFCActivation ? "Activate Your Memorial" : "Welcome to Pontis"}
            </h1>
            <p className="text-xs sm:text-sm text-brand-secondary">
              {invitationId 
                ? "Please sign in to accept your collaboration invitation" 
                : isNFCActivation 
                ? "Sign in or create an account to activate your memorial medallion and claim it to your account" 
                : "Bridging the gap between life and death"
              }
            </p>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {showForgotPassword ? <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} /> : <>
              <h2 className="text-lg sm:text-xl font-serif font-semibold text-brand-primary">
                {isLogin ? "Sign In" : "Sign Up"}
              </h2>
              <p className="text-xs sm:text-sm text-brand-secondary">
                {isLogin ? "Login to your Pontis account" : "Create a free account with Pontis"}
              </p>

              {isLogin ? <>
                  <SignInForm
                    onSubmit={handleSignIn}
                    onForgotPassword={() => setShowForgotPassword(true)}
                    onGoogleSignIn={handleGoogleSignIn}
                    prefillEmail={prefillEmail || undefined}
                    prefillPhone={prefillPhone || undefined}
                  />
                  <p className="text-center text-xs sm:text-sm text-brand-secondary mt-3 sm:mt-4">
                    Don't have an account?{" "}
                    <button onClick={() => setIsLogin(false)} className="text-brand-primary hover:underline font-medium">
                      Create Account
                    </button>
                  </p>
                  <p className="text-center text-xs text-gray-500 mt-2">
                    <a href="https://pontis.life/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-brand-primary transition-colors">
                      Privacy Policy
                    </a>
                    {" | "}
                    <a href="https://pontis.life/policies/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-brand-primary transition-colors">
                      Terms of Service
                    </a>
                  </p>
                </> : <>
                  <SignUpForm
                    onSubmit={handleSignUp}
                    onGoogleSignUp={handleGoogleSignIn}
                    isLoading={isSigningUp}
                    prefillPhone={prefillPhone ? decodeURIComponent(prefillPhone) : undefined}
                  />
                  {signupToken && <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700 font-medium">
                        <span className="font-bold">Token Detected:</span> Your memorial token will be redeemed automatically after sign up.
                      </p>
                    </div>}
                  <p className="text-center text-xs sm:text-sm text-brand-secondary mt-3 sm:mt-4">
                    Already have an account?{" "}
                    <button onClick={() => setIsLogin(true)} className="text-brand-primary hover:underline font-medium">
                      Sign in to existing account
                    </button>
                  </p>
                  <p className="text-center text-xs text-gray-500 mt-2">
                    <a href="https://pontis.life/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-brand-primary transition-colors">
                      Privacy Policy
                    </a>
                    {" | "}
                    <a href="https://pontis.life/policies/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-brand-primary transition-colors">
                      Terms of Service
                    </a>
                  </p>
                </>}
            </>}
        </div>
      </div>
    </div>;
};

export default Auth;
